import { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  Trash2,
  Edit2,
  GripVertical,
  AlertCircle,
  Check,
  X,
  Play,
  Zap,
  Settings2,
  Truck,
  FileText
} from 'lucide-react'
import {
  shippingRulesV2,
  ShippingRuleV2,
  RuleCondition,
  RuleAction,
  ConditionTypeInfo,
  CarrierInfo
} from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

// Tipos locales
interface RuleFormData {
  name: string
  priority: number
  is_active: boolean
  conditions: RuleCondition[]
  actions: RuleAction[]
}

const EMPTY_CONDITION: RuleCondition = {
  type: 'carrier_name_contains',
  value: '',
  operator: 'AND'
}

const EMPTY_ACTION: RuleAction = {
  type: 'set_carrier',
  carrier_code: '',
  service_type: ''
}

const DEFAULT_FORM_DATA: RuleFormData = {
  name: '',
  priority: 0,
  is_active: true,
  conditions: [{ ...EMPTY_CONDITION }],
  actions: [{ ...EMPTY_ACTION }]
}

// Componente principal
export default function ShippingRulesPage() {
  const toast = useToast()
  const [rules, setRules] = useState<ShippingRuleV2[]>([])
  const [loading, setLoading] = useState(true)
  const [conditionTypes, setConditionTypes] = useState<ConditionTypeInfo[]>([])
  const [carriers, setCarriers] = useState<Record<string, CarrierInfo>>({})

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ShippingRuleV2 | null>(null)
  const [formData, setFormData] = useState<RuleFormData>(DEFAULT_FORM_DATA)
  const [saving, setSaving] = useState(false)

  // Test modal state
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)
  const [testData, setTestData] = useState({
    carrier_name: '',
    country_code: 'ES',
    postal_code: '',
    weight: 1,
    total: 50,
    is_cod: false
  })
  const [testResult, setTestResult] = useState<any>(null)

  // Cargar datos iniciales
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [rulesData, conditionsData, carriersData] = await Promise.all([
        shippingRulesV2.getRules(),
        shippingRulesV2.getConditionTypes(),
        shippingRulesV2.getCarriers()
      ])
      setRules(rulesData)
      setConditionTypes(conditionsData.condition_types)
      setCarriers(carriersData.carriers)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Error cargando reglas de envío')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Handlers
  const handleToggle = async (rule: ShippingRuleV2) => {
    try {
      await shippingRulesV2.toggleRule(rule.id, !rule.is_active)
      setRules(prev =>
        prev.map(r => (r.id === rule.id ? { ...r, is_active: !r.is_active } : r))
      )
      toast.success(rule.is_active ? 'Regla desactivada' : 'Regla activada')
    } catch (error) {
      console.error('Error toggling rule:', error)
      toast.error('Error al cambiar estado de la regla')
    }
  }

  const handleDelete = async (rule: ShippingRuleV2) => {
    if (!confirm(`¿Eliminar la regla "${rule.name}"?`)) return

    try {
      await shippingRulesV2.deleteRule(rule.id)
      setRules(prev => prev.filter(r => r.id !== rule.id))
      toast.success('Regla eliminada')
    } catch (error) {
      console.error('Error deleting rule:', error)
      toast.error('Error al eliminar la regla')
    }
  }

  const handleEdit = (rule: ShippingRuleV2) => {
    setEditingRule(rule)
    setFormData({
      name: rule.name,
      priority: rule.priority,
      is_active: rule.is_active,
      conditions: rule.conditions.length > 0 ? rule.conditions : [{ ...EMPTY_CONDITION }],
      actions: rule.actions.length > 0 ? rule.actions : [{ ...EMPTY_ACTION }]
    })
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setEditingRule(null)
    setFormData({ ...DEFAULT_FORM_DATA, priority: rules.length })
    setIsModalOpen(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    if (formData.conditions.length === 0) {
      toast.error('Debes añadir al menos una condición')
      return
    }

    if (formData.actions.length === 0) {
      toast.error('Debes añadir al menos una acción')
      return
    }

    // Validar que las acciones tengan carrier_code
    const hasValidAction = formData.actions.some(a => a.carrier_code)
    if (!hasValidAction) {
      toast.error('Debes seleccionar un transportista en la acción')
      return
    }

    try {
      setSaving(true)

      if (editingRule) {
        const updated = await shippingRulesV2.updateRule(editingRule.id, formData)
        setRules(prev => prev.map(r => (r.id === editingRule.id ? updated : r)))
        toast.success('Regla actualizada')
      } else {
        const created = await shippingRulesV2.createRule(formData)
        setRules(prev => [...prev, created])
        toast.success('Regla creada')
      }

      setIsModalOpen(false)
    } catch (error) {
      console.error('Error saving rule:', error)
      toast.error('Error al guardar la regla')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    try {
      const result = await shippingRulesV2.testRules(testData)
      setTestResult(result)
    } catch (error) {
      console.error('Error testing rules:', error)
      toast.error('Error al probar las reglas')
    }
  }

  // Condition handlers
  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { ...EMPTY_CONDITION }]
    }))
  }

  const removeCondition = (index: number) => {
    if (formData.conditions.length <= 1) return
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }))
  }

  const updateCondition = (index: number, updates: Partial<RuleCondition>) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => (i === index ? { ...c, ...updates } : c))
    }))
  }

  // Action handlers
  const addAction = () => {
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { ...EMPTY_ACTION }]
    }))
  }

  const removeAction = (index: number) => {
    if (formData.actions.length <= 1) return
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }))
  }

  const updateAction = (index: number, updates: Partial<RuleAction>) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => (i === index ? { ...a, ...updates } : a))
    }))
  }

  // Obtener label de condición
  const getConditionLabel = (type: string) => {
    return conditionTypes.find(c => c.type === type)?.label || type
  }

  // Obtener nombre del transportista
  const getCarrierName = (code: string) => {
    return carriers[code]?.name || code.toUpperCase()
  }

  // Obtener nombre del servicio
  const getServiceName = (carrierCode: string, serviceCode: string) => {
    const carrier = carriers[carrierCode]
    if (!carrier) return serviceCode
    const service = carrier.services.find(s => s.code === serviceCode)
    return service?.name || serviceCode
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <Settings2 className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Reglas de envío
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Configura las reglas para asignar transportista automáticamente
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsTestModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
              >
                <Play className="w-4 h-4" />
                Probar reglas
              </button>

              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nueva regla
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {rules.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No hay reglas de envío
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              Crea tu primera regla para asignar transportistas automáticamente
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Crear regla
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className={`bg-white dark:bg-slate-800 rounded-xl border ${
                  rule.is_active
                    ? 'border-teal-200 dark:border-teal-900/50'
                    : 'border-slate-200 dark:border-slate-700'
                } p-4 transition-all hover:shadow-md`}
              >
                <div className="flex items-start gap-4">
                  {/* Drag handle */}
                  <div className="pt-1 cursor-grab text-slate-400 hover:text-slate-600">
                    <GripVertical className="w-5 h-5" />
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(rule)}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      rule.is_active ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        rule.is_active ? 'translate-x-6' : ''
                      }`}
                    />
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-slate-900 dark:text-white">
                        {rule.name}
                      </h3>
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        Prioridad: {rule.priority}
                      </span>
                    </div>

                    {/* Conditions summary */}
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Si:</span>
                      {rule.conditions.map((cond, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && (
                            <span className="text-slate-400 text-xs mx-1">
                              {cond.operator}
                            </span>
                          )}
                          <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs">
                            {getConditionLabel(cond.type)}: {String(cond.value)}
                          </span>
                        </span>
                      ))}
                    </div>

                    {/* Actions summary */}
                    <div className="flex flex-wrap items-center gap-2 text-sm mt-2">
                      <span className="text-slate-500 dark:text-slate-400">Entonces:</span>
                      {rule.actions.map((action, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {action.type === 'set_carrier' && action.carrier_code && (
                            <span className="bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                              <Truck className="w-3 h-3" />
                              {getCarrierName(action.carrier_code)}
                              {action.service_type && (
                                <> - {getServiceName(action.carrier_code, action.service_type)}</>
                              )}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(rule)}
                      className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(rule)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {editingRule ? 'Editar regla' : 'Nueva regla'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Nombre de la regla
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: CTT Baleares"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Conditions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Condiciones (Si)
                </label>
                <div className="space-y-3">
                  {formData.conditions.map((condition, index) => (
                    <div key={index} className="flex items-start gap-2">
                      {index > 0 && (
                        <select
                          value={condition.operator}
                          onChange={(e) =>
                            updateCondition(index, { operator: e.target.value as 'AND' | 'OR' })
                          }
                          className="w-16 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        >
                          <option value="AND">Y</option>
                          <option value="OR">O</option>
                        </select>
                      )}
                      <select
                        value={condition.type}
                        onChange={(e) => updateCondition(index, { type: e.target.value, value: '' })}
                        className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      >
                        {conditionTypes.map((ct) => (
                          <option key={ct.type} value={ct.type}>
                            {ct.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={String(condition.value)}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        placeholder="Valor..."
                        className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      />
                      <button
                        onClick={() => removeCondition(index)}
                        disabled={formData.conditions.length <= 1}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addCondition}
                  className="mt-2 flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                >
                  <Plus className="w-4 h-4" />
                  Agregar criterio
                </button>
              </div>

              {/* Actions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Acciones (Entonces)
                </label>
                <div className="space-y-3">
                  {formData.actions.map((action, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-sm text-slate-500 dark:text-slate-400 py-2">
                        Usar transportista:
                      </span>
                      <select
                        value={action.carrier_code || ''}
                        onChange={(e) => {
                          const carrierCode = e.target.value
                          updateAction(index, {
                            type: 'set_carrier',
                            carrier_code: carrierCode,
                            service_type: ''
                          })
                        }}
                        className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      >
                        <option value="">Seleccionar transportista...</option>
                        {Object.entries(carriers).map(([code, carrier]) => (
                          <option key={code} value={code}>
                            {carrier.name}
                          </option>
                        ))}
                      </select>
                      {action.carrier_code && carriers[action.carrier_code]?.services && (
                        <select
                          value={action.service_type || ''}
                          onChange={(e) => updateAction(index, { service_type: e.target.value })}
                          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        >
                          <option value="">Servicio por defecto</option>
                          {carriers[action.carrier_code].services.map((service) => (
                            <option key={service.code} value={service.code}>
                              {service.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => removeAction(index)}
                        disabled={formData.actions.length <= 1}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addAction}
                  className="mt-2 flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                >
                  <Plus className="w-4 h-4" />
                  Añadir acción
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {isTestModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Probar reglas
              </h2>
              <button
                onClick={() => {
                  setIsTestModalOpen(false)
                  setTestResult(null)
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Método de envío (CMS)
                  </label>
                  <input
                    type="text"
                    value={testData.carrier_name}
                    onChange={(e) => setTestData({ ...testData, carrier_name: e.target.value })}
                    placeholder="Ej: Envío Baleares"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    País
                  </label>
                  <select
                    value={testData.country_code}
                    onChange={(e) => setTestData({ ...testData, country_code: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  >
                    <option value="ES">España</option>
                    <option value="PT">Portugal</option>
                    <option value="FR">Francia</option>
                    <option value="DE">Alemania</option>
                    <option value="IT">Italia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Código postal
                  </label>
                  <input
                    type="text"
                    value={testData.postal_code}
                    onChange={(e) => setTestData({ ...testData, postal_code: e.target.value })}
                    placeholder="07001"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Peso (kg)
                  </label>
                  <input
                    type="number"
                    value={testData.weight}
                    onChange={(e) => setTestData({ ...testData, weight: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Total pedido
                  </label>
                  <input
                    type="number"
                    value={testData.total}
                    onChange={(e) => setTestData({ ...testData, total: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_cod"
                    checked={testData.is_cod}
                    onChange={(e) => setTestData({ ...testData, is_cod: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <label htmlFor="is_cod" className="text-sm text-slate-700 dark:text-slate-300">
                    Contrareembolso
                  </label>
                </div>
              </div>

              {/* Test Result */}
              {testResult && (
                <div
                  className={`p-4 rounded-lg ${
                    testResult.matched
                      ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                      : 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                  }`}
                >
                  {testResult.matched ? (
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-800 dark:text-green-300">
                          Regla coincidente: {testResult.rule_name}
                        </p>
                        <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                          Transportista: {getCarrierName(testResult.carrier_code || '')}
                          {testResult.service_type && (
                            <> - {getServiceName(testResult.carrier_code || '', testResult.service_type)}</>
                          )}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                          Reglas evaluadas: {testResult.all_rules_tested}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800 dark:text-yellow-300">
                          Ninguna regla coincide
                        </p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                          Se usará el transportista por defecto (MRW)
                        </p>
                        <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                          Reglas evaluadas: {testResult.all_rules_tested}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setIsTestModalOpen(false)
                  setTestResult(null)
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={handleTest}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Zap className="w-4 h-4" />
                Probar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
